import { useTranslations } from 'next-intl';
import { makeBreadcrumbs } from '../utils/makeBreadcrumbs';
import { useI18nLabel } from '@/hooks/useI18nLabel';
import { PrefectureValue } from '@/constants/prefectures';
import { useMemo } from 'react';
import { JobBenefitOptionFragment } from '../gen/graphql/JobsRoute.generated';
import { JobCrewCategoryOptionFragment } from '../gen/graphql/JobsRoute.generated';
import { QueryParams } from '../types/QueryParams';
import { Option } from '@/types/options';

export interface UseBreadcrumbsProps {
  prefecture?: Option<PrefectureValue>;
  jobCategory?: JobCrewCategoryOptionFragment;
  jobBenefit?: JobBenefitOptionFragment;
}

export function useBreadcrumbs({ prefecture, jobCategory, jobBenefit }: UseBreadcrumbsProps) {
  const t = useTranslations();
  const translate = useI18nLabel();

  // Create parameter breadcrumb links without labels
  const breadcrumbLinks = useMemo(
    () =>
      makeBreadcrumbs({
        params: {
          prefecture: prefecture?.value,
          job_category: jobCategory?.id,
          job_benefit: jobBenefit?.id,
        } satisfies QueryParams,
        items: [
          ...(prefecture
            ? [
                {
                  key: 'prefecture' as const,
                  label: t(`common.prefecture.label.${prefecture.value}`),
                },
              ]
            : []),
          ...(jobCategory ? [{ key: 'job_category' as const, label: translate(jobCategory) }] : []),
          ...(jobBenefit ? [{ key: 'job_benefit' as const, label: translate(jobBenefit) }] : []),
        ],
        basePath: '/jobs',
      }),
    [prefecture, jobCategory, jobBenefit, t, translate]
  );

  // Add home breadcrumb and apply labels to parameter breadcrumbs
  return useMemo(
    () => [{ label: t('top.title'), href: '/' }, ...breadcrumbLinks],
    [breadcrumbLinks, t]
  );
}
